'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { usePathname } from 'next/navigation';

interface NavigationItem {
    label: string;
    href: string;
}

const Header = () => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const pathname = usePathname();

    const navigationItems: NavigationItem[] = [
        { label: 'For Students', href: '/for-students' },
        { label: 'For Colleges', href: '/for-colleges' },
        { label: 'About', href: '/about' },
    ];

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 20);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        if (isMobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
    }, [isMobileMenuOpen]);

    const isActive = (href: string) => {
        if (href === '/') return pathname === '/';
        return pathname.startsWith(href);
    };

    return (
        <header
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-background/80 backdrop-blur-xl border-b border-white/10 shadow-lg' : 'bg-transparent'
                }`}
        >
            <div className="max-w-7xl mx-auto">
                <div className="relative flex items-center justify-between h-20 px-4 sm:px-6 lg:px-8">
                    {/* Logo Section */}
                    <Link
                        href="/"
                        className="flex items-center gap-3 group focus:outline-none"
                        aria-label="AstikLearn Home"
                    >
                        <div className="relative w-10 h-10 flex items-center justify-center">
                            <div className="absolute inset-0 bg-gradient-to-tr from-primary via-accent to-success rounded-xl opacity-20 group-hover:opacity-40 transition-opacity duration-300 blur-md" />
                            <div className="relative bg-background/50 backdrop-blur-sm rounded-xl p-2 border border-white/10 group-hover:border-primary/50 transition-colors duration-300">
                                <svg
                                    width="24"
                                    height="24"
                                    viewBox="0 0 40 40"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="transform group-hover:scale-110 transition-transform duration-300"
                                >
                                    <defs>
                                        <linearGradient id="logoGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                            <stop offset="0%" stopColor="#0EA5E9" />
                                            <stop offset="50%" stopColor="#06B6D4" />
                                            <stop offset="100%" stopColor="#10B981" />
                                        </linearGradient>
                                    </defs>
                                    <circle cx="20" cy="20" r="18" stroke="url(#logoGradient)" strokeWidth="3" fill="none" />
                                    <path
                                        d="M20 8 L28 16 L24 16 L24 24 L16 24 L16 16 L12 16 Z"
                                        fill="url(#logoGradient)"
                                    />
                                    <circle cx="20" cy="28" r="2.5" fill="url(#logoGradient)" />
                                </svg>
                            </div>
                        </div>
                        <span className="text-xl font-bold tracking-wide bg-clip-text text-transparent bg-gradient-to-r from-white to-white/70 group-hover:to-primary/80 transition-all duration-300">
                            AstikLearn
                        </span>
                    </Link>

                    {/* Desktop Navigation */}
                    <nav className="hidden lg:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 items-center gap-1 bg-white/5 backdrop-blur-md px-2 py-1.5 rounded-full border border-white/10">
                        {navigationItems.map((item) => (
                            <Link
                                key={item.href}
                                href={item.href}
                                className={`relative px-5 py-2 text-sm font-medium rounded-full transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 ${isActive(item.href)
                                    ? 'text-white bg-white/10 shadow-inner'
                                    : 'text-text-secondary hover:text-white hover:bg-white/5'
                                    }`}
                            >
                                {item.label}
                                {isActive(item.href) && (
                                    <span className="absolute bottom-1.5 left-1/2 -translate-x-1/2 w-1 h-1 bg-primary rounded-full shadow-[0_0_8px_rgba(14,165,233,0.8)]" />
                                )}
                            </Link>
                        ))}
                    </nav>

                    {/* Desktop Actions */}
                    {/* <div className="hidden lg:flex items-center gap-3">
                        <Link
                            href="/for-students/login"
                            className="px-5 py-2.5 text-sm font-medium text-text-secondary hover:text-white transition-colors duration-300 focus:outline-none"
                        >
                            Log in
                        </Link>
                        <Link
                            href="/for-students/signup"
                            className="relative group px-6 py-2.5 rounded-lg overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
                        >
                            <div className="absolute inset-0 bg-gradient-to-r from-primary via-accent to-success opacity-90 group-hover:opacity-100 transition-opacity duration-300" />
                            <div className="absolute inset-0 bg-[url('/noise.png')] opacity-10 mix-blend-overlay" />
                            <div className="absolute -inset-full group-hover:inset-full bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 transition-all duration-700 ease-in-out" />
                            <span className="relative flex items-center gap-2 text-sm font-bold text-white tracking-wide">
                                Get Started
                                <Icon name="ArrowRightIcon" size={16} variant="solid" className="group-hover:translate-x-1 transition-transform" />
                            </span>
                        </Link>
                    </div> */}

                    {/* Mobile Menu Button */}
                    <button
                        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                        className="lg:hidden relative p-2 text-text-secondary hover:text-white transition-colors focus:outline-none"
                        aria-label="Toggle mobile menu"
                        aria-expanded={isMobileMenuOpen}
                    >
                        <div className="w-6 h-6 flex flex-col justify-center items-center gap-1.5">
                            <span className={`block w-5 h-0.5 bg-current transform transition-all duration-300 ${isMobileMenuOpen ? 'rotate-45 translate-y-2' : ''}`} />
                            <span className={`block w-5 h-0.5 bg-current transition-opacity duration-300 ${isMobileMenuOpen ? 'opacity-0' : 'opacity-100'}`} />
                            <span className={`block w-5 h-0.5 bg-current transform transition-all duration-300 ${isMobileMenuOpen ? '-rotate-45 -translate-y-2' : ''}`} />
                        </div>
                    </button>
                </div>
            </div>

            {/* Mobile Menu Overlay */}
            <div
                className={`lg:hidden fixed inset-0 z-40 bg-background/95 backdrop-blur-2xl transition-all duration-500 ease-in-out ${isMobileMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-full pointer-events-none'
                    }`}
            >
                <div className="flex flex-col h-full pt-24 px-6 pb-8 overflow-y-auto">
                    <nav className="flex-1 flex flex-col gap-2">
                        {navigationItems.map((item, index) => (
                            <Link
                                key={item.href}
                                href={item.href}
                                onClick={() => setIsMobileMenuOpen(false)}
                                className={`group flex items-center justify-between p-4 rounded-xl border border-white/5 hover:border-white/10 hover:bg-white/5 transition-all duration-300 ${isActive(item.href) ? 'bg-white/5 border-primary/20' : ''
                                    }`}
                                style={{
                                    animation: isMobileMenuOpen ? `fadeInUp 0.5s ease-out ${index * 0.1}s forwards` : 'none',
                                    opacity: 0,
                                }}
                            >
                                <span className={`text-lg font-medium ${isActive(item.href) ? 'text-white' : 'text-text-secondary group-hover:text-white'}`}>
                                    {item.label}
                                </span>
                                <Icon
                                    name="ArrowRightIcon"
                                    size={20}
                                    variant="outline"
                                    className={`text-white/20 group-hover:text-primary transition-colors ${isActive(item.href) ? 'text-primary' : ''}`}
                                />
                            </Link>
                        ))}
                    </nav>

                    <div className="mt-8 space-y-4 pt-8 border-t border-white/10" style={{ animation: isMobileMenuOpen ? 'fadeInUp 0.5s ease-out 0.4s forwards' : 'none', opacity: 0 }}>
                        <Link
                            href="/for-students/login"
                            onClick={() => setIsMobileMenuOpen(false)}
                            className="flex items-center justify-center w-full py-4 text-base font-medium text-text-secondary hover:text-white hover:bg-white/5 rounded-xl border border-transparent hover:border-white/10 transition-all duration-300"
                        >
                            Sign In
                        </Link>
                        <Link
                            href="/for-students/signup"
                            onClick={() => setIsMobileMenuOpen(false)}
                            className="flex items-center justify-center w-full py-4 text-base font-bold text-white bg-gradient-to-r from-primary via-accent to-success rounded-xl shadow-lg shadow-primary/20 active:scale-95 transition-all duration-300"
                        >
                            Start Learning
                        </Link>
                    </div>
                </div>
            </div>

            <style jsx global>{`
                @keyframes fadeInUp {
                    from { opacity: 0; transform: translateY(20px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </header>
    );
};

export default Header;